import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Clock, Users, Check, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addDays, startOfWeek, isSameDay, parseISO, isBefore, startOfDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAYER_OPTIONS = [
  { num: 1, label: 'Singola', desc: '1 giocatore' },
  { num: 2, label: 'Doppia', desc: '2 giocatori' },
  { num: 3, label: 'Tripla', desc: '3 giocatori' },
  { num: 4, label: 'In 4', desc: '4 giocatori' },
];

const BookLessonPage = () => {
  const { maestroId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [maestro, setMaestro] = useState(null);
  const [settings, setSettings] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [numPlayers, setNumPlayers] = useState(1);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [dayHours, setDayHours] = useState([]); // available hours for selectedDate
  const [loadingHours, setLoadingHours] = useState(false);

  useEffect(() => {
    fetchData();
  }, [maestroId]);

  useEffect(() => {
    if (settings?.enabled) fetchBooked();
  }, [weekStart, settings]);

  useEffect(() => {
    if (selectedDate && settings?.enabled) fetchDayHours(selectedDate);
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      const [maestriRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/maestri`, { withCredentials: true }),
        axios.get(`${API_URL}/api/maestri/${maestroId}/lessons/settings`, { withCredentials: true })
      ]);
      const m = maestriRes.data.find(x => x.user_id === maestroId);
      setMaestro(m);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const fetchBooked = async () => {
    try {
      const from = format(weekStart, 'yyyy-MM-dd');
      const to = format(addDays(weekStart, 6), 'yyyy-MM-dd');
      const res = await axios.get(
        `${API_URL}/api/maestri/${maestroId}/lessons/booked?date_from=${from}&date_to=${to}`,
        { withCredentials: true }
      );
      setBookedSlots(res.data);
    } catch (error) {
      console.error('Error fetching booked slots:', error);
    }
  };

  const fetchDayHours = async (date) => {
    setLoadingHours(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const res = await axios.get(
        `${API_URL}/api/maestri/${maestroId}/lessons/available-hours?date=${dateStr}`,
        { withCredentials: true }
      );
      setDayHours(res.data.hours || []);
    } catch (error) {
      setDayHours([]);
    } finally {
      setLoadingHours(false);
    }
  };

  const handleBook = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Seleziona data e orario');
      return;
    }
    setBooking(true);
    try {
      await axios.post(`${API_URL}/api/maestri/${maestroId}/lessons/book`, {
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        num_players: numPlayers
      }, { withCredentials: true });
      toast.success('Lezione prenotata! Il Maestro confermerà a breve.');
      navigate('/lessons/my');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella prenotazione');
    } finally {
      setBooking(false);
    }
  };

  const isSlotBooked = (date, time) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return bookedSlots.some(s => s.date === dateStr && s.time === time);
  };

  const isDayClosed = (day) => {
    if (!settings) return true;
    const dateStr = format(day, 'yyyy-MM-dd');
    const overrides = settings.date_overrides || {};
    if (dateStr in overrides) return overrides[dateStr].length === 0;
    const dow = String(day.getDay()); // 0=Sun, 1=Mon, ...
    const weekly = settings.weekly_schedule || {};
    return (weekly[dow] || []).length === 0;
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = startOfDay(new Date());

  const price = settings?.prices?.[String(numPlayers)] || { 1: 30, 2: 40, 3: 45, 4: 60 }[numPlayers];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-slate-400">Caricamento...</div>
      </div>
    );
  }

  if (!maestro || !settings?.enabled) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Le lezioni non sono disponibili per questo maestro.</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sky-500 font-medium">Torna indietro</button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto animate-fadeIn" data-testid="book-lesson-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(-1)} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-outfit text-2xl font-semibold text-slate-900">Prenota Lezione</h1>
          <p className="text-slate-500 text-sm">con Maestro {maestro.name} {maestro.surname || ''}</p>
        </div>
      </div>

      {/* Maestro Card */}
      <div className="bg-white rounded-2xl p-4 border border-slate-100 flex items-center gap-4 mb-6">
        <div className="w-14 h-14 bg-emerald-400 rounded-2xl flex items-center justify-center text-white text-xl font-bold overflow-hidden ring-2 ring-emerald-100">
          {getAvatarUrl(maestro) ? (
            <img src={getAvatarUrl(maestro)} alt={maestro.name} className="w-full h-full rounded-2xl object-cover" />
          ) : maestro.name?.charAt(0)}
        </div>
        <div>
          <p className="font-semibold text-slate-900">{maestro.name} {maestro.surname || ''}</p>
          <p className="text-xs text-emerald-600 font-medium">Maestro</p>
        </div>
      </div>

      {/* Player Count / Price */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-3">
          <Users className="w-4 h-4 inline mr-2" />
          Tipo di Lezione
        </label>
        <div className="grid grid-cols-2 gap-2">
          {PLAYER_OPTIONS.map(opt => (
            <button
              key={opt.num}
              type="button"
              onClick={() => setNumPlayers(opt.num)}
              data-testid={`lesson-type-${opt.num}`}
              className={`p-3 rounded-xl text-center transition-all border ${
                numPlayers === opt.num
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                  : 'bg-slate-50 border-slate-100 text-slate-600 hover:border-slate-200'
              }`}
            >
              <span className="block text-lg font-bold">
                {settings.prices?.[String(opt.num)] || { 1: 30, 2: 40, 3: 45, 4: 60 }[opt.num]}€
              </span>
              <span className="text-xs font-medium">{opt.label}</span>
              <span className="block text-xs text-slate-400">{opt.desc}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3 text-center">Durata 1 ora, campo compreso</p>
      </div>

      {/* Week Calendar */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 mb-4">
        <div className="flex items-center justify-between mb-4">
          <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" />
            Scegli Data e Orario
          </label>
          <div className="flex gap-1">
            <button
              onClick={() => setWeekStart(addDays(weekStart, -7))}
              data-testid="week-prev"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setWeekStart(addDays(weekStart, 7))}
              data-testid="week-next"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Day selector */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {weekDays.map((day) => {
            const isPast = isBefore(day, today);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const dateStr = format(day, 'yyyy-MM-dd');
            const closed = isDayClosed(day);
            const disabled = isPast || closed;

            return (
              <button
                key={dateStr}
                onClick={() => { if (!disabled) { setSelectedDate(day); setSelectedTime(null); } }}
                disabled={disabled}
                data-testid={`day-${dateStr}`}
                className={`py-2 rounded-xl text-center transition-all ${
                  disabled
                    ? 'opacity-30 cursor-not-allowed'
                    : isSelected
                      ? 'bg-emerald-400 text-white shadow-md'
                      : 'hover:bg-slate-100 text-slate-700'
                }`}
              >
                <span className="block text-xs text-current/60 capitalize">
                  {format(day, 'EEE', { locale: it })}
                </span>
                <span className="block text-sm font-semibold">{format(day, 'd')}</span>
              </button>
            );
          })}
        </div>

        {/* Time slots */}
        {selectedDate && (
          <div>
            <p className="text-xs text-slate-500 mb-2 capitalize">
              {format(selectedDate, 'EEEE d MMMM', { locale: it })}
            </p>
            {loadingHours ? (
              <div className="py-8 text-center text-sm text-slate-400 animate-pulse">Caricamento orari...</div>
            ) : dayHours.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400">Nessun orario disponibile</div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {dayHours.map(time => {
                  const booked = isSlotBooked(selectedDate, time);
                  const isSelected = selectedTime === time;

                  return (
                    <button
                      key={time}
                      onClick={() => { if (!booked) setSelectedTime(time); }}
                      disabled={booked}
                      data-testid={`slot-${time}`}
                      className={`py-2.5 rounded-xl text-sm font-medium transition-all ${
                        booked
                          ? 'bg-slate-100 text-slate-300 cursor-not-allowed line-through'
                          : isSelected
                            ? 'bg-emerald-400 text-white shadow-md'
                            : 'bg-slate-50 text-slate-600 hover:bg-emerald-50 hover:text-emerald-600'
                      }`}
                    >
                      {time}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary & Book */}
      {selectedDate && selectedTime && (
        <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-200 mb-4" data-testid="booking-summary">
          <h3 className="font-medium text-emerald-800 mb-2">Riepilogo</h3>
          <div className="space-y-1 text-sm text-emerald-700">
            <p>Lezione <strong>{PLAYER_OPTIONS.find(o => o.num === numPlayers)?.label}</strong> con Maestro {maestro.name}</p>
            <p className="capitalize">{format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })} alle <strong>{selectedTime}</strong></p>
            <p className="text-lg font-bold text-emerald-800 mt-2">{price}€ <span className="text-sm font-normal">/ 1 ora, campo compreso</span></p>
          </div>
        </div>
      )}

      <button
        onClick={handleBook}
        disabled={booking || !selectedDate || !selectedTime}
        data-testid="confirm-booking-btn"
        className="w-full py-4 bg-emerald-500 text-white rounded-full font-medium text-lg hover:bg-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {booking ? (
          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            <Check className="w-5 h-5" />
            Prenota Lezione
          </>
        )}
      </button>
    </div>
  );
};

export default BookLessonPage;
